import type { ReactNode } from 'react';
import { IconButton, Search } from '@carbon/react';
import {
  ExpandAll,
  CollapseAll,
  ChevronUp,
  ChevronDown,
} from '@carbon/react/icons';
import { DownloadButton } from 'components/download-button';
import { SearchTips } from 'components/documents-search';
import { SortBy } from './sort-by';
import { EXPAND_ALL_LIMIT, type ExpandAllBroadcast } from './utils';
import styles from './results-section.module.scss';

interface CardsToolbarProps {
  // Leads the toolbar (what the page puts there).
  start?: ReactNode;
  label: string;
  searchInput: string;
  onSearchInput: (value: string) => void;
  showSearchTips: boolean;
  showSortBy: boolean;
  rowCount: number;
  // The card prev/next steps from.
  navAnchor: number;
  onNavigate: (offset: number) => void;
  expandAll?: ExpandAllBroadcast;
  onExpandAll: (broadcast: ExpandAllBroadcast) => void;
  // Why the export can't start, if it can't.
  exportReason?: string;
  exporting: boolean;
  onExport: () => void;
}

/**
 * The runsheet toolbar: search, Sort By, prev/next, expand/collapse all and
 * CSV export.
 * @param {CardsToolbarProps} props The toolbar.
 * @return {JSX.Element} The toolbar.
 */
export function CardsToolbar({
  start,
  label,
  searchInput,
  onSearchInput,
  showSearchTips,
  showSortBy,
  rowCount,
  navAnchor,
  onNavigate,
  expandAll,
  onExpandAll,
  exportReason,
  exporting,
  onExport,
}: CardsToolbarProps) {
  return (
    <div className={styles.searchRow}>
      {start}
      <Search
        size="lg"
        labelText={`Search ${label}`}
        placeholder={`Search ${label}`}
        value={searchInput}
        onChange={(e) => onSearchInput(e.target.value)}
      />
      {showSearchTips && <SearchTips iconOnly />}
      {showSortBy && <SortBy />}
      <IconButton
        kind="ghost"
        label="Previous document"
        size="md"
        align="bottom"
        autoAlign
        onClick={() => onNavigate(-1)}
        disabled={rowCount === 0 || navAnchor <= 0}
      >
        <ChevronUp />
      </IconButton>
      <IconButton
        kind="ghost"
        label="Next document"
        size="md"
        align="bottom"
        autoAlign
        onClick={() => onNavigate(1)}
        disabled={rowCount === 0 || navAnchor >= rowCount - 1}
      >
        <ChevronDown />
      </IconButton>
      <IconButton
        kind="ghost"
        label="Expand all"
        size="md"
        align="bottom"
        autoAlign
        onClick={() => onExpandAll({ open: true })}
        disabled={rowCount === 0}
      >
        <ExpandAll />
      </IconButton>
      <IconButton
        kind="ghost"
        label="Collapse all"
        size="md"
        align="bottom-end"
        autoAlign
        onClick={() => onExpandAll({ open: false })}
        disabled={rowCount === 0}
      >
        <CollapseAll />
      </IconButton>
      {expandAll?.open && rowCount > EXPAND_ALL_LIMIT && (
        <span className={styles.expandAllCap}>
          First {EXPAND_ALL_LIMIT} expanded
        </span>
      )}
      <DownloadButton
        hasIconOnly
        size="md"
        iconDescription={exportReason ?? 'Export as CSV (.csv)'}
        tooltipPosition="bottom"
        loading={exporting}
        loadingText="Preparing…"
        disabled={Boolean(exportReason)}
        onClick={onExport}
      />
    </div>
  );
}
